/**
 * Phase 2J-C / Item 3 — Admin-managed partner branding inline panel.
 * Embedded in the partner detail modal. Admin uploads/replaces/removes
 * a partner logo and edits display name + contact details. Used by the
 * proposal PDF builder when issuing trade-referral proposals.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Image as ImgIcon, Loader2, Trash2, Upload } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const ALLOWED = new Set(["image/png", "image/jpeg", "image/webp"]);
const MAX_BYTES = 1 * 1024 * 1024;

// Phase 2L-A120 — FastAPI returns 422 validation errors as
// `detail = [{type, loc, msg, input, ctx, url}, ...]`. Sonner's toast
// can only render strings/ReactNodes; an array of objects triggers
// React minified error #31. Always flatten to a friendly string
// before showing the toast.
function humanError(e, fallback) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => (typeof d === "string" ? d : d?.msg))
      .filter(Boolean);
    if (parts.length) return parts.join(" · ");
  }
  if (detail && typeof detail === "object" && typeof detail.msg === "string") {
    return detail.msg;
  }
  return fallback || "Something went wrong. Please try again.";
}

export default function PartnerBrandingSection({ partnerId, partnerEmail }) {
  const [branding, setBranding] = useState(null);
  const [edits, setEdits] = useState({ display_name: "", phone: "", email: "", website: "", pws_report_disabled: false });
  const [logoUrl, setLogoUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/admin/partners/${partnerId}/branding`);
      setBranding(data);
      setEdits({
        display_name: data.display_name || "",
        phone: data.phone || "",
        email: data.email || "",
        website: data.website || "",
        pws_report_disabled: !!data.pws_report_disabled,
      });
      if (logoUrl) URL.revokeObjectURL(logoUrl);
      if (data.has_logo) {
        try {
          const blob = await api.get(
            `/admin/partners/${partnerId}/branding/logo/file`,
            { responseType: "blob" },
          );
          setLogoUrl(URL.createObjectURL(blob.data));
        } catch { setLogoUrl(null); }
      } else { setLogoUrl(null); }
    } catch (e) {
      toast.error(humanError(e, "Failed to load partner branding"));
    }
  };

  useEffect(() => { if (partnerId) refresh(); }, [partnerId]); // eslint-disable-line
  useEffect(() => () => { if (logoUrl) URL.revokeObjectURL(logoUrl); }, [logoUrl]);

  const saveFields = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/partners/${partnerId}/branding`, edits);
      toast.success("Branding updated");
      refresh();
    } catch (e) {
      toast.error(humanError(e, "Save failed"));
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!ALLOWED.has(file.type)) { toast.error("PNG, JPG, or WebP only"); return; }
    if (file.size > MAX_BYTES) { toast.error("Logo exceeds 1 MB limit"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/admin/partners/${partnerId}/branding/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo uploaded");
      refresh();
    } catch (e) {
      toast.error(humanError(e, "Upload failed"));
    } finally { setUploading(false); }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove this partner's logo?")) return;
    try {
      await api.delete(`/admin/partners/${partnerId}/branding/logo`);
      toast.success("Logo removed");
      refresh();
    } catch (e) {
      toast.error(humanError(e, "Remove failed"));
    }
  };

  return (
    <div data-testid="partner-branding-section" className="mt-5 rounded-2xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Branding (admin-managed)</div>
          <div className="text-xs text-slate-500">Used on trade-referral estimate PDFs. Partners cannot self-upload.</div>
        </div>
      </div>

      <div className="flex gap-4 items-start">
        <div className="flex-shrink-0 w-32 h-20 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden">
          {logoUrl
            ? <img src={logoUrl} alt="partner logo" className="max-w-full max-h-full object-contain" />
            : <ImgIcon className="w-7 h-7 text-slate-300" />}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex gap-2">
            <input
              ref={fileInput}
              data-testid="partner-logo-input"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => uploadLogo(e.target.files?.[0])}
            />
            <Button
              data-testid="partner-logo-upload"
              size="sm"
              variant="outline"
              disabled={uploading}
              onClick={() => fileInput.current?.click()}
              className="rounded-full text-xs"
            >
              {uploading
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Uploading…</>
                : <><Upload className="w-3.5 h-3.5 mr-1.5" />{branding?.has_logo ? "Replace logo" : "Upload logo"}</>}
            </Button>
            {branding?.has_logo && (
              <Button
                data-testid="partner-logo-remove"
                size="sm"
                variant="outline"
                onClick={removeLogo}
                className="rounded-full text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />Remove
              </Button>
            )}
          </div>
          <div className="text-[10px] text-slate-400">PNG, JPG, or WebP · max 1 MB</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-4">
        <div>
          <Label className="text-xs">Display name</Label>
          <Input data-testid="partner-display-name" value={edits.display_name}
                 onChange={(e) => setEdits({ ...edits, display_name: e.target.value })}
                 placeholder={branding?.business_name || "Business name"} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Phone</Label>
          <Input data-testid="partner-branding-phone" value={edits.phone}
                 onChange={(e) => setEdits({ ...edits, phone: e.target.value })}
                 className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Email</Label>
          <Input data-testid="partner-branding-email" type="email" value={edits.email}
                 onChange={(e) => setEdits({ ...edits, email: e.target.value })}
                 placeholder={partnerEmail || ""} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Website</Label>
          <Input data-testid="partner-branding-website" value={edits.website}
                 onChange={(e) => setEdits({ ...edits, website: e.target.value })}
                 placeholder="https://" className="mt-1" />
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-3">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            data-testid="partner-pws-report-toggle"
            type="checkbox"
            checked={!!edits.pws_report_disabled}
            onChange={(e) => setEdits({ ...edits, pws_report_disabled: e.target.checked })}
            className="mt-1 w-4 h-4 rounded border-amber-400 text-amber-700 focus:ring-amber-500"
          />
          <span className="text-xs leading-relaxed">
            <span className="font-semibold text-amber-900 block">Disable PWS Pristine Report for this partner</span>
            <span className="text-amber-800 block mt-0.5">
              When on, customer-facing combined PDFs for this partner&apos;s bookings will be COA-only.
              The Pristine Plain-English Summary is suppressed AND PWS-product commissions for this partner are not credited.
              SCWT-service commissions (water tests, memberships) are unaffected.
            </span>
          </span>
        </label>
      </div>

      <Button
        data-testid="partner-branding-save"
        onClick={saveFields}
        disabled={saving}
        size="sm"
        className="mt-3 rounded-full text-xs btn-aqua font-semibold"
      >
        {saving ? "Saving…" : "Save branding"}
      </Button>
    </div>
  );
}
